import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';

import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetProjectStatisticsQuery } from 'src/store/services/analytics.service';

export function SalesDistributionChart() {
  const { address, network } = useAnalyticsContext();

  const {
    data: projectStats,
    isLoading,
    error,
  } = useGetProjectStatisticsQuery(
    {
      page: 1,
      size: 6,
      address,
      network,
    },
    { skip: !address || !network },
  );

  // Calculate chart data from API response
  const { chartData, chartLabels } = useMemo(() => {
    if (!projectStats?.data?.length) {
      return { chartData: [], chartLabels: [] };
    }

    const total = projectStats.data.reduce((sum, project) => sum + project.revenues, 0);
    const data = projectStats.data.map(project => (total > 0 ? (project.revenues / total) * 100 : 0));
    const labels = projectStats.data.map(project => project.name || 'Unknown Project');

    return {
      chartData: data,
      chartLabels: labels,
    };
  }, [projectStats]);

  const chartOptions: ApexOptions = {
    chart: {
      type: 'donut',
      background: 'transparent',
      fontFamily: 'Gotham',
      width: 520,
    },
    theme: {
      mode: 'dark',
    },
    colors: ['#495DB6', '#7C6DF9', '#4CC3C1', '#F6B936', '#EE7081', '#F83A38'],
    labels: chartLabels,
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: 'right',
      offsetY: 0,
      itemMargin: {
        vertical: 6,
      },
      markers: {
        strokeWidth: 0,
        size: 8,
        shape: 'square',
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '50%',
        },
      },
    },
    stroke: {
      show: false,
    },
    tooltip: {
      theme: 'dark',
      style: {
        fontSize: '12px',
        fontFamily: 'Gotham',
      },
      y: {
        formatter: (value: number) => `${value.toFixed(1)}%`,
      },
    },
  };

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: colors.white,
          fontFamily: 'Gotham',
          marginBottom: 3,
        }}>
        Sales Distribution by Projects
      </Typography>

      <Stack direction='row' spacing={3} sx={{ flex: 1 }}>
        {isLoading ? (
          <Box sx={{ width: 480, height: 296, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Skeleton variant='circular' width={250} height={250} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
          </Box>
        ) : error ? (
          <Box sx={{ width: 480, height: 296, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: colors.red200, textAlign: 'center' }}>
              Failed to load sales distribution data
            </Typography>
          </Box>
        ) : chartData?.length > 0 ? (
          <Box sx={{ width: 480, height: 296 }}>
            <Chart options={chartOptions} series={chartData} type='donut' width='100%' height='100%' />
          </Box>
        ) : (
          <Box sx={{ width: 480, height: 296, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: colors.neutral90, textAlign: 'center' }}>
              No sales distribution data available
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
